import * as am4core from '@amcharts/amcharts4/core';
import { MouseCursorStyle } from '@amcharts/amcharts4/core';
import { ForceDirectedSeries, ForceDirectedTree } from '@amcharts/amcharts4/plugins/forceDirected';
import Am4themesAnimated from '@amcharts/amcharts4/themes/animated';
import { makeStyles } from '@material-ui/core';
import { useParams } from '@reach/router';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactGA from 'react-ga';
import { useTranslation } from 'react-i18next';

import { useClassifierState } from '../../../../../../contexts/ClassifiersContext/ClassifiersContext';
import { ClassifiersTypes } from '../../../../../../contexts/ClassifiersContext/ClassifiersContext.types';
import { api } from '../../../../../../core/api';
import { CONSTANTS } from '../../../../../../core/config/constants';
import { getActiveDataset, getActiveDbVersion } from '../../../../../../core/utils/utils';
import { OwnershipGraphResult } from '../../../../../../generated/model/ownershipGraphResult';
import { useAppGoogleReCaptcha } from '../../../../../../hooks/useAppGoogleReCaptcha';
import { useIsMount } from '../../../../../../hooks/useOnChange';
import { useSnackbarMessage } from '../../../../../../hooks/useSnackbarMessage';
import { useBenchmarkState } from '../../../../../Benchmark/Context/BenchmarkContext';
import { useCompaniesState } from '../../../../shared/CompanyContext/CompanyContext';
import { useErrorsDispatch } from '../../../../shared/ErrorContext/ErrorContext';
import { ErrorContextActionTypes } from '../../../../shared/ErrorContext/ErrorContext.types';
import {
  useOwnershipConnectionsDispatch,
  useOwnershipConnectionsState,
} from '../OwnershipConnectionsContext/OwnershipConnectionsContext';
import { OwnershipConnectionsContextActionTypes } from '../OwnershipConnectionsContext/OwnershipConnectionsContext.types';
import {
  exportMenu,
  OwnershipGraphNodeTypeEnum,
  OwnershipMappedGraphNode,
  OwnershipNodesColorEnum,
} from './OwnershipConnectionsChart.type';
import {
  generateNodes,
  setExportingData,
  setNodeCoordinates,
  setNodeLinks,
} from './OwnershipConnectionsChart.utils';

am4core.useTheme(Am4themesAnimated);

interface Props {}

const OwnershipConnectionsChart: React.FC<Props> = () => {
  const {
    companyId,
    onlyBeneficialOwnership,
    includeHistoricalOwnership,
    showAllConnections,
  } = useOwnershipConnectionsState();
  const dispatch = useOwnershipConnectionsDispatch();
  const dispatchErrors = useErrorsDispatch();
  const isFirstMount = useIsMount();
  const [links, setLinks] = useState<any>({});
  const { executeRecaptcha } = useAppGoogleReCaptcha();
  const chart = useRef(null);
  const classes = useStyles();
  const [apiData, setApiData] = useState<OwnershipGraphResult>();
  const [chartData, setChartData] = useState<OwnershipMappedGraphNode[]>([]);
  const [expandedNodeIds, setExpandedNodeIds] = useState<Set<string>>(new Set());
  const [expandedOthersGroups, setExpandedOthersGroups] = useState<Set<string>>(new Set());
  const [hasHiddenNodes, setHasHiddenNodes] = useState<boolean>(false);
  const { t } = useTranslation();
  const { companyUid, dataset: datasetParam, dbVersion: dbVersionParam } = useParams<{
    companyUid?: string;
    dataset?: string;
    dbVersion?: string;
  }>();
  const classifierRegionCountry = useClassifierState(ClassifiersTypes.RegionAndCountry);

  const { dataset, dbVersion } = useCompaniesState();
  const { activeBenchmark } = useBenchmarkState();
  const datasetSource = datasetParam ?? dataset;
  const dbVersionSource = dbVersionParam ?? dbVersion;
  const activeDataset = getActiveDataset(datasetSource, activeBenchmark);
  const activeDbVersion = getActiveDbVersion(dbVersionSource, activeBenchmark);

  useEffect(() => {
    if (isFirstMount && companyUid) {
      dispatch({
        type: OwnershipConnectionsContextActionTypes.SetCompanyId,
        payload: companyUid,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (companyId) {
      const getNewConnections = async () => {
        try {
          const recaptchaToken = await executeRecaptcha?.('search');
          const data = await api.company.getOwnershipConnections(
            companyId,
            recaptchaToken!,
            activeDataset,
            activeDbVersion,
          );

          dispatch({
            type: OwnershipConnectionsContextActionTypes.PushToPath,
            payload: { id: companyId, name: data.name! },
          });

          setApiData(data);
          setExpandedNodeIds(new Set());
          setExpandedOthersGroups(new Set());
        } catch (error) {
          dispatchErrors({
            type: ErrorContextActionTypes.SetError,
            payload: { error },
          });
        }
      };
      getNewConnections();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, activeDataset, activeDbVersion]);

  useEffect(() => {
    const countryCodeToNameMap: Map<string, string> = new Map();
    classifierRegionCountry
      .filter((c) => c.id && c.name)
      .forEach((c) => countryCodeToNameMap.set(c.id!, c.name!));

    if (apiData) {
      const { nodes, mappedLinks, linkPairs, hasHiddenNodes: hasHidden } = generateNodes(
        apiData,
        countryCodeToNameMap,
        onlyBeneficialOwnership,
        includeHistoricalOwnership,
        expandedNodeIds,
        expandedOthersGroups,
        showAllConnections,
      );
      setLinks(mappedLinks);
      setHasHiddenNodes(hasHidden);

      const nodesWithLinks = setNodeLinks(nodes, linkPairs);
      const nodesWithCoordinate = setNodeCoordinates(nodesWithLinks);

      setChartData(nodesWithCoordinate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    apiData,
    onlyBeneficialOwnership,
    includeHistoricalOwnership,
    showAllConnections,
    expandedNodeIds,
    expandedOthersGroups,
  ]);

  // reset per-branch expansion when the caps are lifted, so unticking returns to a clean default
  // view - keeping the same reference when already empty avoids a redundant node-generation pass
  useEffect(() => {
    setExpandedNodeIds((prev) => (prev.size ? new Set() : prev));
    setExpandedOthersGroups((prev) => (prev.size ? new Set() : prev));
  }, [showAllConnections]);

  useSnackbarMessage(hasHiddenNodes, 'connections:snackbarNotAllConnectionsText', {
    variant: 'info',
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'right',
    },
  });

  useLayoutEffect(() => {
    const x = am4core.create('chartdiv', ForceDirectedTree);
    const series = x.series.push(new ForceDirectedSeries());
    series.tooltip = new am4core.Tooltip();
    const mappedData = chartData.map((d) => {
      if (d.isSynthetic === 'expand') {
        return {
          ...d,
          name:
            d.expandState === 'expanded'
              ? t('connections:ownership.collapseBranch')
              : t('connections:ownership.expandMore', { count: d.hiddenCount }),
          countryLabel: '',
          nodeValueText: '',
        };
      }
      if (d.isSynthetic === 'others') {
        return {
          ...d,
          name:
            d.expandState === 'expanded'
              ? t('connections:ownership.collapseOthers')
              : t('connections:ownership.othersGroup', { count: d.hiddenCount }),
          countryLabel: '',
          nodeValueText: '',
        };
      }
      return {
        ...d,
        name: d.name ?? CONSTANTS.UNKNOWN_NAME,
        countryLabel: d.countryLabel ? `\n${d.countryLabel}` : '',
        nodeValueText: d?.nodeValueText
          ? d.nodeValueText
          : d?.nodeValue || d?.nodeValue === 0
          ? `(${d.nodeValue}%)`
          : '',
      };
    });

    const levelNodesMap = chartData.reduce((acc: Map<number, number>, curr) => {
      if (!acc.get(curr.level)) {
        acc.set(curr.level, 0);
      }
      acc.set(curr.level, acc.get(curr.level)! + 1);
      return acc;
    }, new Map<number, number>());
    const maxLevelNodes = Math.max(...Array.from(levelNodesMap.values()));
    const scaleMultiplier = maxLevelNodes > 100 ? 0.5 : 1.5 - maxLevelNodes / 100;
    const fontSize = maxLevelNodes > 30 ? 12 : 14;
    // init export menu
    x.exporting.menu = new am4core.ExportMenu();
    x.exporting.menu.align = 'left';
    x.exporting.menu.verticalAlign = 'top';

    x.exporting.menu.items = [exportMenu];
    // init export menu data for download csv
    setExportingData(series);

    series.data = mappedData;
    series.fontSize = 10 * scaleMultiplier;
    series.minRadius = 60;
    series.maxRadius = 160;
    series.centerStrength = 1;
    series.manyBodyStrength = -20;

    // Set up data fields
    series.dataFields.id = 'id';
    series.dataFields.name = 'name';
    series.dataFields.linkWith = 'linkWith';
    series.dataFields.color = 'color';
    series.dataFields.fixed = 'fixed';

    // Add labels
    series.nodes.template.label.text = '{name} {nodeValueText} {countryLabel}';
    series.nodes.template.label.valign = 'top';
    series.nodes.template.label.fill = am4core.color(OwnershipNodesColorEnum.LABEL);
    series.nodes.template.label.width = 200;
    series.nodes.template.label.wrap = true;
    series.nodes.template.label.dy = 5;
    series.nodes.template.togglable = true;
    series.nodes.template.propertyFields.x = 'x';
    series.nodes.template.propertyFields.y = 'y';
    series.nodes.template.circle.disabled = true;
    series.nodes.template.circle.cursorOverStyle = MouseCursorStyle.pointer;

    series.nodes.template.events.on('hit', (event) => {
      const node: any = event.target;
      const item = node.dataItem.dataContext;
      if (item.isSynthetic === 'expand') {
        setExpandedNodeIds((prev) => {
          const next = new Set(prev);
          if (next.has(item.expandTargetId)) {
            next.delete(item.expandTargetId);
          } else {
            next.add(item.expandTargetId);
          }
          return next;
        });
        return;
      }
      if (item.isSynthetic === 'others') {
        setExpandedOthersGroups((prev) => {
          const next = new Set(prev);
          if (next.has(item.othersGroupKey)) {
            next.delete(item.othersGroupKey);
          } else {
            next.add(item.othersGroupKey);
          }
          return next;
        });
        return;
      }
      if (item.companyUid && item.companyUid !== companyId) {
        ReactGA.event({ category: 'OWNERSHIP_CHART', action: 'COMPANY_NODE_CLICK' });
        dispatch({
          type: OwnershipConnectionsContextActionTypes.SetCompanyId,
          payload: item.companyUid,
        });
      }
    });

    const hoverState = series.nodes.template.states.create('highlight');
    hoverState.properties.scale = 1.3;

    series.nodes.template.events.on('over', function (ev) {
      const node: any = ev.target;
      const item = node.dataItem.dataContext;
      if (
        item.isSynthetic ||
        (item.id &&
          (!item.type || item.type === OwnershipGraphNodeTypeEnum.COMPANY) &&
          item.country + item.id !== companyId)
      ) {
        ev.target.setState('highlight');
      }
    });

    series.nodes.template.events.on('out', function (ev) {
      ev.target.setState('default');
    });

    // Add icons
    // Configure icons
    const icon = series.nodes.template.createChild(am4core.Sprite);
    icon.propertyFields.path = 'path';
    icon.horizontalCenter = 'middle';
    icon.verticalCenter = 'bottom';
    icon.scale = scaleMultiplier;
    icon.cursorOverStyle = MouseCursorStyle.pointer;

    const solidBackground = series.nodes.template.createChild(am4core.Rectangle);
    solidBackground.width = 40;
    solidBackground.height = 40;
    solidBackground.x = -20;
    solidBackground.y = -30;
    solidBackground.cursorOverStyle = MouseCursorStyle.pointer;
    solidBackground.opacity = 0;

    series.links.template.adapter.add('tooltipText', function (text = '', { target, source }: any) {
      const from: { id: string } = source.dataItem.dataContext;
      const to: { id: string } = target.dataItem.dataContext;

      if (from?.id && to?.id) {
        const linkIdFrom = `${from.id}_${to.id}`;
        const linkIdTo = `${to.id}_${from.id}`;
        const link = links[linkIdFrom] ?? links[linkIdTo];
        return link?.value
          ? `[font-size: ${fontSize}px; ${OwnershipNodesColorEnum.LABEL}]${link?.value}`
          : '';
      }

      return text;
    });

    // set color for links
    series.links.template.adapter.add('stroke', () => {
      return am4core.color(OwnershipNodesColorEnum.LINK);
    });

    series.links.template.adapter.add('strokeDasharray', function (
      dash = '',
      { target, source }: any,
    ) {
      const from: { id: string } = source.dataItem.dataContext;
      const to: { id: string } = target.dataItem.dataContext;

      if (from?.id && to?.id) {
        const linkIdFrom = `${from.id}_${to.id}`;
        const linkIdTo = `${to.id}_${from.id}`;
        const link = links[linkIdFrom] ?? links[linkIdTo];
        return link?.active ? dash : '10,4';
      }
      return dash;
    });

    series.tooltip.getStrokeFromObject = false;
    series.links.template.showOnInit = true;
    series.links.template.interactionsEnabled = true;
    series.links.template.showTooltipOn = 'always';
    series.links.template.strokeWidth = 2;
    series.links.template.distance = 3;
    series.links.template.strokeOpacity = 1;
    series.tooltip.background.stroke = am4core.color({ r: 0, g: 0, b: 0, a: 0 });
    series.links.template.fill = am4core.color({ r: 0, g: 0, b: 0, a: 0 });

    // Events
    x.events.on('drag', () => {
      x.validateData();
    });
    x.events.on('wheel', () => {
      x.validateData();
    });
    // Zoom
    x.zoomable = true;
    // x.maxZoomLevel = 4;

    chart.current = x as any;

    return () => {
      x.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData]);

  return <div id="chartdiv" className={classes.chartBox}></div>;
};

const useStyles = makeStyles((theme) => ({
  chartBox: {
    flexGrow: 1,
    width: '100%',
    height: '100%',
    background: theme.palette.background.paper,
    boxShadow: '0 0 5px 0 rgba(0,0,0,0.5)',
    '& p': {
      margin: 0,
    },
    '& .hidden': {
      display: 'none',
    },
  },
}));

export default OwnershipConnectionsChart;
